import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { notification, theme, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import { getDesignEffectivenessColumns } from 'constants/designEffectivenessColumns';
import { ControlRow, QueryScope } from 'store/ducks/InternalControls/types';
import { internalControlsOp } from 'store/ducks/InternalControls';
import { useLocation } from 'react-router-dom';
import { useQueryParams, withDefault, ArrayParam } from 'use-query-params';
import dayjs from 'dayjs';
import qs from 'qs';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import * as Styled from '../../RiskControl.styled';
import * as LayoutStyled from 'components/Layouts/Layout.styled';

const { useToken } = theme;

const DesignEffectivenessTesting: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { drawTable, selectedEntityId, lang } = useSelector(
    (state: RootState) => ({
      drawTable: state.internalControls.drawTable,
      selectedEntityId: state.internalControls.selectedEntityId,
      lang: state.global.lang
    })
  );

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [controls, setControls] = useState<ControlRow[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [newControlIds, setNewControlIds] = useState<Set<number>>(new Set());
  const originalControlsRef = useRef<ControlRow[]>([]);
  const dispatch = useDispatch();

  // Simple tracking function that marks data as dirty
  const trackingSetControls = (
    newControls: ControlRow[] | ((prev: ControlRow[]) => ControlRow[])
  ) => {
    if (typeof newControls === 'function') {
      setControls((prev) => {
        const result = newControls(prev);
        setIsDirty(true);
        return result;
      });
    } else {
      setControls(newControls);
      setIsDirty(true);
    }
  };

  useEffect(() => {
    dispatch(
      internalControlsOp.setHasChanges(isDirty || newControlIds.size > 0)
    );
  }, [isDirty, newControlIds, dispatch]);

  const [performerToD, setPerformerToD] = useState<
    { label: string; value: number }[]
  >([]);

  const [designEffectiveness, setDesignEffectiveness] = useState<
    { label: string; value: number }[]
  >([]);

  const [testObjectiveness, setTestObjectiveness] = useState<
    { label: string; value: number }[]
  >([]);

  const [approvers, setApprovers] = useState<
    { label: string; value: number }[]
  >([]);

  const location = useLocation();

  const queryParams = qs.parse(location.search, {
    ignoreQueryPrefix: true
  });

  const [testingMethod, setTestingMethod] = useState<
    { label: string; value: number }[]
  >([]);
  const { token } = useToken();
  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));
  const FiltersParam = withDefault(ArrayParam, []);

  const [query, setQuery] = useQueryParams({
    group: FiltersParam,
    division: FiltersParam,
    region: FiltersParam,
    legal_entity: FiltersParam,
    business_unit: FiltersParam,
    control_owner: FiltersParam,
    bru_assigned: FiltersParam
  });

  const bruAssignedFilter = (query.bru_assigned?.[0] as string) || undefined;

  const getValidIdFromArray = (value: unknown): number | undefined => {
    if (!Array.isArray(value) || value.length === 0) return undefined;

    const num = Number(value[0]);
    return Number.isInteger(num) && num > 0 ? num : undefined;
  };

  const buildSingleControlParamFromQuery = () => {
    const params: Record<string, number> = {};

    const groupId = getValidIdFromArray(query.group);
    if (groupId !== undefined) {
      params.group_id = groupId;
    } else {
      const divisionId = getValidIdFromArray(query.division);
      if (divisionId !== undefined) {
        params.division_id = divisionId;
      } else {
        const regionId = getValidIdFromArray(query.region);
        if (regionId !== undefined) {
          params.region_id = regionId;
        } else {
          const legalEntityId = getValidIdFromArray(query.legal_entity);
          if (legalEntityId !== undefined) {
            params.legal_entity_id = legalEntityId;
          } else {
            const businessUnitId = getValidIdFromArray(query.business_unit);
            if (businessUnitId !== undefined) {
              params.business_unit_id = businessUnitId;
            }
          }
        }
      }
    }

    const controlOwnerId = getValidIdFromArray(query.control_owner);
    if (controlOwnerId !== undefined) {
      params.control_owner_id = controlOwnerId;
    }

    return params;
  };

  const scopeKey = useMemo(() => {
    const scope = buildSingleControlParamFromQuery();
    return JSON.stringify(scope);
  }, [query]);

  useEffect(() => {
    const fetchControls = async () => {
      try {
        const params = buildSingleControlParamFromQuery();
        if (bruAssignedFilter) {
          (params as any).bru_assigned = bruAssignedFilter;
        }
        const response = await ApiService.getControlsTestBPR(params);
        const apiControls = response.data.data.filter(
          (item: any) => item.key_control === true
        );

        const mappedControls = apiControls.map((item: any) => {
          const instance = item.instances?.[0] ?? {};

          return {
            key: item.id.toString(),
            id: item.id,

            // ───────── CONTROL LEVEL ─────────
            controlName: item.control_name,
            controlDescription: item.control_description,
            keyControl: item.key_control || false,
            linkedRisks: item.linked_risk_maintenance ?? [],
            notUsed: item.not_used === true,

            testingMethod: {
              id: item.testing_method_id ?? null
            },

            // ───────── INSTANCE LEVEL ─────────
            instance_id: instance?.instance_id ?? null,

            performerToD: instance.performer_tod
              ? typeof instance.performer_tod === 'string'
                ? instance.performer_tod
                : {
                    id: instance.performer_tod?.id ?? null,
                    name: instance.performer_tod?.name ?? null
                  }
              : null,

            approvers: instance.approver
              ? typeof instance.approver === 'string'
                ? instance.approver
                : {
                    id: instance.approver?.id ?? null,
                    name: instance.approver?.name ?? null
                  }
              : null,

            designEffectiveness: {
              id: instance.design_effectiveness?.id ?? null,
              name: instance.design_effectiveness?.name ?? null
            },

            testObjectiveness:
              instance.test_objectiveness || item.test_objectiveness
                ? typeof (
                    instance.test_objectiveness || item.test_objectiveness
                  ) === 'string'
                  ? instance.test_objectiveness || item.test_objectiveness
                  : {
                      id:
                        (instance.test_objectiveness?.id ||
                          item.test_objectiveness?.id) ??
                        null,
                      name:
                        (instance.test_objectiveness?.name ||
                          item.test_objectiveness?.name) ??
                        null
                    }
                : null,

            testDescription: instance?.test_description ?? '',
            evidenceDescription: instance?.evidence_description ?? '',
            evidenceFile1: instance?.evidence_file_1 ?? '',
            lastTestDateDesign: instance?.last_test_date_design ?? '',
            nextTestDate: instance?.next_test_date ?? '',

            control_image: item.controlImage ?? null,
            active: item.active ?? true,
            comments: item.comments ?? null,
            activeInstance: instance?.instanceActive ?? true,
            commentsInstance: instance?.instanceComments ?? null,
            start_date: instance?.instanceStartDate ?? null,
            evidence_file_1: instance?.evidenceFile1 ?? null
          };
        });

        setControls(mappedControls);
        originalControlsRef.current = JSON.parse(
          JSON.stringify(mappedControls)
        );
        setIsDirty(false);

        dispatch(internalControlsOp.setDrawTable(false));
      } catch (error) {
        console.error('Error fetching controls:', error);
      }
    };

    fetchControls();
  }, [drawTable, dispatch, scopeKey, bruAssignedFilter]);

  useEffect(() => {
    const fetchAllDropdowns = async () => {
      try {
        const [
          approversResponse,
          performerToDRes,
          designEffRes,
          testObjectivenessRes,
          testingMethodRes
        ] = await Promise.all([
          ApiService.getApproversBPR(),
          ApiService.getPerformerToDBPR(),
          ApiService.getDesignEffectivenessBPR(),
          ApiService.getTestObjectivenessBPR(),
          ApiService.getTestingMethodBPR()
        ]);

        setApprovers(
          approversResponse.data.data.map((approver: any) => ({
            label: approver.name,
            value: approver.id
          }))
        );

        setPerformerToD(
          performerToDRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setDesignEffectiveness(
          designEffRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestObjectiveness(
          testObjectivenessRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestingMethod(
          testingMethodRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );
      } catch (err) {
        console.error('Failed to fetch dropdown data', err);
      }
    };

    fetchAllDropdowns();
  }, []);

  const buildNormalizedScope = (): QueryScope => {
    const singleScope = buildSingleControlParamFromQuery();
    return {
      group_id: singleScope.group_id ?? null,
      division_id: singleScope.division_id ?? null,
      region_id: singleScope.region_id ?? null,
      legal_entity_id: singleScope.legal_entity_id ?? null,
      business_unit_id: singleScope.business_unit_id ?? null
    };
  };

  const isScopedView = useMemo(() => {
    const scope = buildSingleControlParamFromQuery();
    return Object.keys(scope).length > 0;
  }, [query]);

  const controlColumns = useMemo(
    () =>
      getDesignEffectivenessColumns(
        testingMethod,
        approvers,
        controls,
        performerToD,
        designEffectiveness,
        testObjectiveness,
        setControls,
        trackingSetControls,
        newControlIds,
        isScopedView,
        t
      ),
    [
      testingMethod,
      approvers,
      controls,
      performerToD,
      designEffectiveness,
      testObjectiveness,
      newControlIds,
      isScopedView,
      t
    ]
  );
  const preparePayload = (controlsPerRow: ControlRow[], scope: QueryScope) => {
    const hasScope = Object.values(scope).some(
      (value) => value !== null && value !== undefined
    );

    return {
      data_list: controlsPerRow.map((ctrl) => {
        const basePayload = {
          id: ctrl.id,
          control_name: ctrl.controlName ?? null,
          control_description: ctrl.controlDescription ?? null,
          control_image: ctrl.controlImage ?? null,
          active: ctrl.active ?? true,
          comments: ctrl.comments ?? null,
          start_date: ctrl.startDate ?? null,
          end_date: ctrl.endDate ?? null,
          testing_method: ctrl.testingMethod?.id ?? null,
          test_objectiveness_id_2:
            typeof ctrl.testObjectiveness === 'string'
              ? ctrl.testObjectiveness
              : ctrl.testObjectiveness?.id ?? null
        };

        if (!hasScope) {
          return basePayload;
        }

        return {
          ...basePayload,
          instance: {
            instance_id: ctrl.instance_id ?? null,
            active: ctrl.instanceActive ?? true,
            comments: ctrl.instanceComments ?? null,

            group: scope.group_id ?? null,
            division: scope.division_id ?? null,
            region: scope.region_id ?? null,
            legal_entity: scope.legal_entity_id ?? null,
            business_unit: scope.business_unit_id ?? null,

            performer_tod:
              typeof ctrl.performerToD === 'string'
                ? ctrl.performerToD
                : ctrl.performerToD?.id ?? null,
            approver:
              typeof ctrl.approvers === 'string'
                ? ctrl.approvers
                : ctrl.approvers?.id ?? null,
            design_effectiveness: ctrl.designEffectiveness?.id ?? null,

            last_test_date_design: ctrl.lastTestDateDesign
              ? dayjs(ctrl.lastTestDateDesign).format('YYYY-MM-DD')
              : null,
            start_date: ctrl.instanceStartDate ?? null,

            evidence_description: ctrl.evidenceDescription ?? null,
            evidence_file_1: ctrl.evidenceFile1 ?? null,
            test_description: ctrl.testDescription ?? '',
            assertion_area_2_id: 2
          }
        };
      })
    };
  };

  const handleControlSave = async () => {
    try {
      if (!isDirty && newControlIds.size === 0) {
        notification.info({
          message: 'No Changes',
          description: 'No changes detected to save.',
          placement: 'topRight',
          duration: 3
        });
        return;
      }

      // Find changed controls by comparing with original
      const changedControls = controls.filter((control) => {
        if (newControlIds.has(control.id)) return true;
        const original = originalControlsRef.current.find(
          (c) => c.id === control.id
        );
        if (!original) return false;

        // Check if control has actually changed
        return JSON.stringify(control) !== JSON.stringify(original);
      });

      if (changedControls.length === 0) {
        notification.info({
          message: 'No Changes',
          description: 'No changes detected to save.',
          placement: 'topRight',
          duration: 3
        });
        return;
      }
      const scope = buildNormalizedScope();
      const payload = preparePayload(changedControls, scope);
      const response = await ApiService.saveControlsTestBPR(payload);

      originalControlsRef.current = JSON.parse(JSON.stringify(controls));
      setNewControlIds(new Set());
      setIsDirty(false);
      dispatch(internalControlsOp.setHasChanges(false));
      dispatch(internalControlsOp.setDrawTable(true));

      notification.success({
        message: 'Controls Saved',
        description:
          response?.data?.message ||
          `${changedControls.length} control(s) saved successfully.`,
        placement: 'topRight',
        duration: 3
      });
    } catch (error: unknown) {
      console.error('Save error:', error);

      const errorMessage =
        (
          error as {
            response?: { data?: { message?: string; error?: string } };
          }
        )?.response?.data?.message ||
        (
          error as {
            response?: { data?: { message?: string; error?: string } };
          }
        )?.response?.data?.error ||
        (error as Error)?.message ||
        'Unknown error occurred';
      const statusCode =
        (error as { response?: { status?: unknown } })?.response?.status ||
        'Unknown';

      notification.error({
        message: `Save Failed (${statusCode})`,
        description: errorMessage,
        placement: 'topRight',
        duration: 10
      });
    }
  };

  return (
    <InternalControlLayout onSave={handleControlSave}>
      <>
        <Styled.DesignEffectivenessWrapper>
          <CustomTable
            drawTable={true}
            rowKey='key'
            columns={controlColumns}
            dataSource={controls}
            itemsCount={controls.length}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: controls.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page: number, size: number) => {
                setCurrentPage(page);
                setPageSize(size);
              }
            }}
            rowClassName={(record: { notUsed: boolean }) =>
              record.notUsed ? 'disabled-row has-row-tooltip' : ''
            }
            onRow={(record: { notUsed: boolean }) => {
              if (!record.notUsed) return {};

              return {
                'data-tooltip': t('DISABLED_DUE_TO_RISK_CONTROL_TYPE_CHANGE')
              };
            }}
            scroll={{ x: 2500 }}
          />
        </Styled.DesignEffectivenessWrapper>
      </>
    </InternalControlLayout>
  );
};

export default DesignEffectivenessTesting;
